import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { customerMsalInstance, staffMsalInstance } from "@/auth/msalConfig";
import { authApi } from "@/api/endpoints";
import { useAuthStore } from "@/store/auth";
import { Loader2 } from "lucide-react";

export function CustomerCallbackPage() {
  return <CallbackPage tenantType="customer" />;
}
export function StaffCallbackPage() {
  return <CallbackPage tenantType="staff" />;
}

function CallbackPage({ tenantType }: { tenantType: "customer" | "staff" }) {
  const navigate = useNavigate();
  const setAuth = useAuthStore((s) => s.setAuth);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const instance = tenantType === "customer" ? customerMsalInstance : staffMsalInstance;
        await instance.initialize();
        const response = await instance.handleRedirectPromise();
        const accountResult = response ?? (instance.getAllAccounts()[0] ? {
          idToken: (await instance.acquireTokenSilent({
            scopes: ["openid", "profile", "email"],
            account: instance.getAllAccounts()[0],
          })).idToken,
        } as { idToken: string } : null);
        if (!accountResult?.idToken) throw new Error("No token returned");
        const data = await authApi.exchange(accountResult.idToken, tenantType);
        if (cancelled) return;
        setAuth(data.access_token, data.refresh_token, data.user);
        navigate(tenantType === "staff" ? "/staff" : "/dashboard", { replace: true });
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : "Authentication failed";
        if (!cancelled) setError(msg);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tenantType, setAuth, navigate]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="card p-8 max-w-md">
          <h1 className="text-lg font-semibold text-red-700 mb-2">Sign-in error</h1>
          <p className="text-sm text-slate-600 mb-4">{error}</p>
          <a href={tenantType === "staff" ? "/staff/login" : "/"} className="btn-primary">Try again</a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-3 text-slate-600">
      <Loader2 className="animate-spin" size={32} />
      <p className="text-sm">Completing sign-in…</p>
    </div>
  );
}
